import React, { useEffect, useState } from 'react';
import {
    Box, Chip, CircularProgress, TextField, Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { listProductDefinitionsService } from '../../../services/pac.service';

/** Backend shape: { [product_key]: { enabled: boolean, config?: object } } */
export const normalizeProductAccessOverrides = (val) => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) return {};
    const normalized = {};
    Object.entries(val).forEach(([key, entry]) => {
        if (entry === true) {
            normalized[key] = { enabled: true };
        } else if (entry === false) {
            normalized[key] = { enabled: false };
        } else if (entry && typeof entry === 'object' && entry.enabled !== undefined) {
            normalized[key] = {
                enabled: !!entry.enabled,
                ...(entry.config ? { config: entry.config } : {}),
            };
        }
    });
    return normalized;
};

// Labels can be any type coming from the products list (strings, numbers, …).
const toDisplayLabel = (product) => {
    const raw = product && (product.label !== undefined && product.label !== null && product.label !== ''
        ? product.label
        : product.product_key);
    return String(raw !== undefined && raw !== null ? raw : '');
};

/**
 * Single chip-input picker for plan `default_product_access_overrides`.
 *
 * - Pick a product from the dropdown -> it is added as an "Enabled" chip.
 * - Remove a chip with the cross -> the product goes back to "Not set"
 *   (its key is absent from the saved JSON, so the plan default applies).
 *
 * Saved JSON shape is unchanged: { [product_key]: { enabled: true, config? } }
 */
const ProductAccessOverridesPicker = ({ value, onChange, disabled, error, helperText }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const overrides = normalizeProductAccessOverrides(value);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        listProductDefinitionsService()
            .then((res) => {
                if (!mounted) return;
                const list = (res && res.data && res.data.products) ? res.data.products : [];
                setProducts(list.filter((p) => p.is_active !== false));
            })
            .catch(() => {
                if (mounted) setProducts([]);
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <Box display="flex" alignItems="center" style={{ gap: 8, padding: '8px 0' }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">Loading products…</Typography>
            </Box>
        );
    }

    if (products.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary">
                No products found. Add products under the Products tab first.
            </Typography>
        );
    }

    // Chips = products currently enabled in the JSON.
    const selectedProducts = products.filter((p) => {
        const entry = overrides[p.product_key];
        return !!(entry && entry.enabled);
    });

    // Selected -> { enabled: true } (config preserved); removed -> key dropped (Not set).
    const handleSelectionChange = (event, newSelection) => {
        const next = {};
        newSelection.forEach((p) => {
            const prev = overrides[p.product_key];
            next[p.product_key] = {
                enabled: true,
                ...(prev && prev.config ? { config: prev.config } : {}),
            };
        });
        onChange(next);
    };

    return (
        <Box>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                Default Product Access
            </Typography>
            <Autocomplete
                multiple
                size="small"
                options={products}
                getOptionLabel={toDisplayLabel}
                getOptionSelected={(opt, val) => opt.product_key === val.product_key}
                value={selectedProducts}
                onChange={handleSelectionChange}
                disabled={disabled}
                disableCloseOnSelect
                renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                    <Chip
                        icon={<CheckCircleIcon style={{ color: '#fff', fontSize: 16 }} />}
                        label={toDisplayLabel(option)}
                        size="small"
                        {...getTagProps({ index })}
                        style={{
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                            fontWeight: 500,
                        }}
                    />
                ))}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        margin="dense"
                        label="Enabled products"
                        placeholder={selectedProducts.length === 0 ? 'Select products to enable…' : ''}
                    />
                )}
            />
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 2 }}>
                Selected products are enabled for this plan · Cross (×) removes = Not set (plan default)
            </Typography>
            {(error || helperText) && (
                <Typography
                    variant="caption"
                    color={error ? 'error' : 'textSecondary'}
                    display="block"
                    style={{ marginTop: 4 }}
                >
                    {error || helperText}
                </Typography>
            )}
        </Box>
    );
};

export default ProductAccessOverridesPicker;
